import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

interface ImageData {
  fileName: string;
  fullPath: string;
  url: string;
  size: number;
  uploaded: string;
  etag: string;
}

interface GalleryResponse {
  success: boolean;
  data: {
    pasta: string;
    images: ImageData[];
    total: number;
  };
}

export function ImageGalleryPage() {
  const [searchParams] = useSearchParams();
  const pastaParam = searchParams.get('p') || 'api';
  
  // // Função para selecionar logo baseada no índice da imagem e minuto atual
  // const getWatermarkLogo = (imageIndex: number) => {
  //   const now = new Date();
  //   const minute = now.getMinutes();
  //   // Combina minuto e índice da imagem para criar variação
  //   const logoIndex = (minute + imageIndex) % WAFFLE_BRANDS.length;
  //   return WAFFLE_BRANDS[logoIndex].logo;
  // };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [images, setImages] = useState<ImageData[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchImages = async (pasta: string, pageNum: number = 1, isLoadMore: boolean = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError('');
      }

      // Simular paginação client-side já que a API não suporta paginação
      const response = await fetch(`https://backend.testeswaffle.org/utils/images?pasta=${pasta}`);
      
      if (!response.ok) {
        throw new Error('Erro ao carregar imagens');
      }

      const data: GalleryResponse = await response.json();

      if (data.success) {
        // Ordenar imagens por data de upload (mais recentes primeiro)
        const sortedImages = data.data.images.sort((a, b) => 
          new Date(b.uploaded).getTime() - new Date(a.uploaded).getTime()
        );
        
        // Simular paginação - 8 imagens por página (melhor performance)
        const itemsPerPage = 100;
        const startIndex = (pageNum - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedImages = sortedImages.slice(0, endIndex);
        
        if (isLoadMore) {
          setImages(paginatedImages);
        } else {
          setImages(paginatedImages);
        }
        
        setHasMore(endIndex < sortedImages.length);
      } else {
        throw new Error('Erro ao carregar imagens');
      }
    } catch (error) {
      console.error('Erro:', error);
      setError(error instanceof Error ? error.message : 'Erro ao carregar imagens');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (pastaParam) {
      setPage(1);
      fetchImages(pastaParam, 1);
    }
  }, [pastaParam]);

  // Scroll infinito
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 1000 &&
        hasMore &&
        !loading &&
        !loadingMore
      ) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchImages(pastaParam, nextPage, true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [page, hasMore, loading, loadingMore, pastaParam]);


  return (
    <div className="min-h-screen bg-white">
      {/* Loading */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-100 text-red-600 px-4 py-2 rounded z-50">
          {error}
        </div>
      )}

      {/* Images Grid - Masonry layout */}
      {!loading && !error && images.length > 0 && (
        <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-6 2xl:columns-7 gap-0">
          {images.map((image, _index) => (
            <div
              key={image.fileName}
              className="break-inside-avoid relative"
            >
              <img
                src={image.url}
                alt=""
                className="w-full h-auto block transition-opacity duration-300"
                loading="lazy"
                decoding="async"
                style={{ opacity: 0 }}
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.opacity = '1';
                }}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.opacity = '0.3';
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="flex justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  );
} 